import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cx } from './cx.ts';

/**
 * Field Notes button. The ONLY sanctioned way to render a button —
 * pages must not hand-compose button className strings (DESIGN.md §6).
 *
 * Variants:
 *   - `primary`   — forest fill; one per view region.
 *   - `secondary` — hairline outline on card; the default workhorse.
 *   - `ghost`     — chrome-free; toolbars and table row actions.
 *   - `danger`    — outlined `bad`; destructive intents. Pair with a
 *     confirm dialog — a danger button never mutates directly.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'md' | 'sm';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-forest font-medium text-on-forest hover:bg-forest-bright active:bg-forest-deep',
  secondary:
    'border border-line bg-card font-medium text-ink-mid hover:border-line-strong hover:text-ink',
  ghost: 'font-medium text-ink-mid hover:bg-active/60 hover:text-ink',
  danger: 'border border-bad-line font-medium text-bad hover:bg-bad-tint',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  md: 'h-9 gap-1.5 px-4 text-label',
  sm: 'h-7 gap-1 px-2.5 text-caption font-medium',
};

const BASE_CLASSES =
  'inline-flex select-none items-center justify-center whitespace-nowrap rounded transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40 disabled:pointer-events-none disabled:opacity-50';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly loading?: boolean;
  readonly children: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = 'secondary', size = 'md', loading = false, disabled, className, children, type, ...rest },
  ref,
): JSX.Element {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={cx(BASE_CLASSES, VARIANT_CLASSES[variant], SIZE_CLASSES[size], className)}
      {...rest}
    >
      {loading ? <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" /> : null}
      {children}
    </button>
  );
});

/**
 * Exposed for the rare anchor-styled-as-button case (e.g. download
 * links). Everything interactive that *acts* should be a `<Button>`.
 */
export function buttonClass(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md'): string {
  return cx(BASE_CLASSES, VARIANT_CLASSES[variant], SIZE_CLASSES[size]);
}
